/**
 * /terms — Terms of Service.
 * Operator: Thoughtplanet Advisory Services LLP.
 */
import { LegalShell } from "@/components/LegalShell";

export function TermsPage() {
  return (
    <LegalShell title="Terms of Service" effectiveDate="June 10, 2026">
      <p>
        These Terms of Service (&ldquo;Terms&rdquo;) govern your use of the
        website, application, and services (collectively the
        &ldquo;Service&rdquo;) operated by{" "}
        <strong>Thoughtplanet Advisory Services LLP</strong>, a limited
        liability partnership registered in India (&ldquo;Curvle,&rdquo;
        &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;). By
        accessing or using Curvle, you agree to be bound by these Terms.
      </p>

      <h2>1. Eligibility</h2>
      <p>
        You must be at least 13 years old to use Curvle. If you are between
        13 and 18 (or the age of majority in your jurisdiction), you may use
        the Service only with the involvement and consent of a parent or
        legal guardian. By using Curvle you represent that you meet this
        requirement.
      </p>

      <h2>2. The Service</h2>
      <p>
        Curvle is a daily, browser-based football free-kick puzzle game.
        Each day a new puzzle is published, and players have one attempt
        per day. The Service is offered in two tiers:
      </p>
      <ul>
        <li>
          <strong>Free</strong>: full access to the daily puzzle, daily
          streaks, and the global leaderboard.
        </li>
        <li>
          <strong>Curvle Pro</strong>: a one-time payment for lifetime
          access that unlocks additional features such as practice mode,
          the daily puzzle archive, friend leagues, the stats dashboard,
          streak insurance, and bracket bonus content.
        </li>
      </ul>
      <p>
        Curvle is a pure skill-based game. Outcomes are determined by
        deterministic physics applied to your input parameters. There is no
        gambling, no real-money prize, no chance-based reward mechanic, no
        in-game currency, and no betting.
      </p>

      <h2>3. Accounts &amp; Authentication</h2>
      <p>
        Curvle can be played anonymously via a long-lived device cookie. To
        save your streak across devices, claim a leaderboard handle, or
        purchase Pro, you may sign in using Google OAuth or an email
        magic-link. You are responsible for maintaining the confidentiality
        of any sign-in credentials and for all activity under your account.
      </p>
      <p>
        We may suspend or terminate accounts that violate these Terms,
        including for suspected automation, manipulation of scores, abuse of
        other players, or fraudulent payment activity.
      </p>

      <h2>4. Acceptable Use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>
          Use bots, scripts, or any automated means to interact with the
          Service or generate scores.
        </li>
        <li>
          Reverse-engineer, decompile, or attempt to derive the source code
          or physics parameters of the Service.
        </li>
        <li>
          Scrape, mass-download, or republish puzzle content, leaderboard
          data, or other player attempts without our prior written consent.
        </li>
        <li>
          Submit content via display names or other input fields that is
          unlawful, harassing, threatening, defamatory, obscene, or that
          impersonates another person or entity.
        </li>
        <li>
          Attempt to disrupt, overload, or compromise the security of the
          Service or its infrastructure.
        </li>
        <li>
          Resell, sublicense, or commercially exploit access to Pro
          features.
        </li>
      </ul>

      <h2>5. Curvle Pro</h2>
      <h3>5.1 Pricing</h3>
      <p>
        Curvle Pro is a one-time payment for lifetime access. There is no
        recurring charge, no auto-renewal, and nothing to cancel. The
        current price is displayed on the <a href="/pricing">pricing page</a>{" "}
        and at checkout, exclusive of any applicable taxes. We reserve the
        right to change prices for future purchases; existing Pro accounts
        are unaffected by any such change.
      </p>

      <h3>5.2 Payment processors</h3>
      <p>
        Payments are processed by Gumroad, Inc., acting as our Merchant of
        Record. Gumroad is the seller of record for the transaction and you
        also agree to Gumroad&rsquo;s applicable terms. We may engage
        additional payment providers in future; this section is updated when
        that happens.
      </p>

      <h3>5.3 Refunds</h3>
      <p>
        Refunds are governed by our <a href="/refund">refund policy</a>.
        In summary: a 7-day self-serve money-back guarantee. Beyond 7 days,
        duplicate or fraudulent charges are always refundable and bug/outage
        cases are reviewed on request. Gumroad, our Merchant of Record,
        processes refunds on our behalf and reserves the right to issue a
        refund within 90 days at its discretion to prevent chargebacks.
      </p>

      <h2>6. Intellectual Property</h2>
      <p>
        Curvle, the Curvle name and logo, the puzzles, the engine, and all
        related materials are owned by Thoughtplanet Advisory Services LLP
        and protected by applicable intellectual-property laws. Subject to
        these Terms, we grant you a limited, non-exclusive, non-transferable
        licence to access and use the Service for personal, non-commercial
        play.
      </p>
      <p>
        Player-generated content (such as display names) remains owned by
        you, but you grant Curvle a worldwide, royalty-free licence to
        display that content in connection with operating the Service
        (e.g. on leaderboards).
      </p>

      <h2>7. Third-Party Services</h2>
      <p>
        The Service relies on third-party providers including (without
        limitation) Railway (hosting), Upstash (Redis), Resend (transactional
        email), and our payment providers. By using Curvle you also agree
        that data necessary to operate these services may be transmitted to
        them, as further described in our{" "}
        <a href="/privacy">privacy policy</a>.
      </p>

      <h2>8. Disclaimers</h2>
      <p>
        The Service is provided on an &ldquo;as is&rdquo; and &ldquo;as
        available&rdquo; basis. To the maximum extent permitted by law, we
        disclaim all warranties, whether express or implied, including
        warranties of merchantability, fitness for a particular purpose, and
        non-infringement. We do not warrant that the Service will be
        uninterrupted or error-free.
      </p>

      <h2>9. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by law, in no event will
        Thoughtplanet Advisory Services LLP or its partners, employees, or
        agents be liable for any indirect, incidental, special,
        consequential, or punitive damages, or any loss of profits or
        revenue, arising out of or in connection with your use of the
        Service. Our total aggregate liability for any claim arising out of
        or relating to the Service shall not exceed the amount you paid to
        us in the twelve (12) months preceding the event giving rise to the
        claim, or USD 100, whichever is greater.
      </p>

      <h2>10. Indemnity</h2>
      <p>
        You agree to indemnify and hold harmless Curvle and its partners
        from and against any claims, damages, liabilities, and expenses
        arising out of your breach of these Terms or your misuse of the
        Service.
      </p>

      <h2>11. Termination</h2>
      <p>
        You may stop using the Service at any time and may delete your
        account from the in-app account settings (the deletion is
        immediate and cascades to your saved attempts and refresh tokens).
        We may suspend or terminate your access to the Service, including
        any Curvle Pro access, if you breach these Terms or for any other
        reason permitted by law; in case of termination by us for cause,
        no refund will be due.
      </p>

      <h2>12. Changes to These Terms</h2>
      <p>
        We may update these Terms from time to time. If we make material
        changes, we will provide reasonable notice (e.g. by email or
        in-app notice) before the new Terms take effect. Continued use of
        the Service after the effective date constitutes acceptance of the
        revised Terms.
      </p>

      <h2>13. Governing Law &amp; Jurisdiction</h2>
      <p>
        These Terms are governed by the laws of India, without regard to
        its conflict-of-laws principles. The courts at Hyderabad,
        Telangana, India shall have exclusive jurisdiction over any
        dispute arising out of or in connection with these Terms or the
        Service, subject to any mandatory consumer-protection rules in
        your country of residence.
      </p>

      <h2>14. Contact</h2>
      <p>
        Questions about these Terms can be sent to{" "}
        <a href="mailto:vbraju@thoughtplanet.com">vbraju@thoughtplanet.com</a>.
      </p>
    </LegalShell>
  );
}
